import { CloudDownloadOutlined } from "@material-ui/icons";
import api from "api";
import { APIFormulation } from "api/formulations";
import { APIRequirement } from "api/requirements";
import {
  SnapshotFormulation,
  SnapshotPercentage,
  SnapshotRequirement,
} from "api/types/snapshot";
import Button from "components/Button";
import FormulationApprovalButton, {
  FormulationApprovalPanel,
} from "components/FormulationApproval";
import FormulationFilter from "components/FormulationFilter";
import Loading from "components/Loading";
import ProjectLockedLabel from "components/ProjectLockedLabel";
import SearchFormulations from "components/SearchFormulations";
import UploadButton from "components/UploadButton";
import ValidationDashboard from "components/ValidationDashboard";
import { CsvValidationErrorContext } from "components/ValidationDashboard/contexts";
import { useValidationSummary } from "components/ValidationDashboard/hooks";
import { addCsvSepHeader, reparseMapHeadersFromCsvString } from "csv/utils";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { observer } from "mobx-react-lite";
import React, { useCallback, useState } from "react";
import { ScrollSync } from "react-scroll-sync";
import { useAsync, useUnmount } from "react-use";
import { useStore } from "store";
import styled from "styled-components";
import { Toast } from "utils";
import Toolbar from "../../../components/Toolbar";
import { useProject, useSave } from "../../Workspace/hooks";
import { CustomSheet } from "../../Workspace/types";
import GenerateCostSheetButton from "../GenerateCostSheetButton";
import FormulationsTabAdditives from "./FormulationsTabAdditives";
import FormulationsTabBaseOils from "./FormulationsTabBaseOils";
import FormulationsTabHead from "./FormulationsTabHead";
import FormulationsTabTotals from "./FormulationsTabTotals";
import FormulationsTabTreatRates from "./FormulationsTabTreatRates";

type Props = {
  tab: CustomSheet;
};

const SheetContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 0;
  overflow: auto;
  /* padding: 1.25rem; */
`;

const FormulationsTab: React.FC<Props> = ({ tab }) => {
  const store = useStore();
  const project = useProject();
  const currentUser = store.auth.current;


  const hasEditRole = store.auth.getPermissions('define', 'formulations')?.toLowerCase() === 'edit';
  const hasEditPermission = !!(
    currentUser &&
    ( hasEditRole && 
      (project?.opsInputUsers.includes(currentUser.id) ||
      project?.adminUsers.includes(currentUser.id)))
  );

  const editable = hasEditPermission && !project?.isLocked && !tab.locked;
  const showSignOff = editable && store.auth.isRbacAdmin;

  const [activeSheet, setActiveSheet] = useState<string>();
  const [showApprovalPanel, setShowApprovalPanel] = useState(false);
  const [approvalExpanded, setApprovalExpanded] = useState(false);

  const validationSummary = useValidationSummary();

  useUnmount(() => {
    if (project && project.isDirty) {
      project.setNotDirty();
    }
  });

  // (Re)load formulations when user opens this tab.
  const state = useAsync(async () => {
    if (!project) {
      return;
    }
    // When the project is locked and we have a snapshot, populate the stores
    // from the snapshot so the sheets render frozen data without making a
    // network request (locked projects generally return no rows to viewers
    // through the live API because of cluster / user-scoped permissions).
    const snapshotFormulations = project.snapshot?.formulations;
    const snapshotRequirements = project.snapshot?.requirements;
    if (project.isLocked && snapshotFormulations?.length) {
      // The snapshot backend stores value-first data (names) in the primary
      // keys and keeps the real IDs / component-material details in parallel
      // `*Id` / `*Ids` / `*Rcode` / `*Description` / `*CrdStatus` fields.
      // `saveListItem` expects the classic API shape (IDs for M2M fields,
      // and full `{id, rcode, description, crdStatus}` objects for
      // base-oil / additive / treat-rate percentages). Reconstruct that
      // shape before hydrating the store, otherwise the grid renders
      // blank formulation columns.
      const normalizePercentage = (
        p: SnapshotPercentage,
        key: "baseOil" | "additive" | "treatRate"
      ) => {
        const idKey = `${key}Id` as const;
        const rcodeKey = `${key}Rcode` as const;
        const descKey = `${key}Description` as const;
        const crdKey = `${key}CrdStatus` as const;
        return {
          id: p.id,
          [key]: {
            id: p[idKey] ?? 0,
            rcode: p[rcodeKey] ?? "",
            description: p[descKey] ?? "",
            crdStatus: p[crdKey] ?? "",
          },
          // Model percentage maps are `Map<identifier, string>`; the
          // snapshot stores `value` as `number | null`.
          value: p.value == null ? "" : String(p.value),
        };
      };

      // Defensive coercion helpers. Snapshot payloads have accumulated a
      // few years of schema evolution (older locked projects predate the
      // parallel `*Id` / `*Name` fields) so we can't rely on any particular
      // shape — coerce everything to what the mobx-keystone Formulation
      // props actually expect before handing it to `saveListItem`.
      const asNumber = (v: unknown): number | null => {
        if (typeof v === "number" && Number.isFinite(v)) return v;
        if (typeof v === "string" && v.trim() !== "" && !isNaN(Number(v))) {
          return Number(v);
        }
        return null;
      };
      const asId = (v: unknown): number | undefined => {
        const n = asNumber(v);
        return n === null ? undefined : n;
      };
      const asIdArray = (v: unknown): number[] =>
        Array.isArray(v)
          ? v
              .map((x) => asNumber(x))
              .filter((x): x is number => x !== null)
          : [];
      const asStringArray = (v: unknown): string[] =>
        Array.isArray(v) ? v.map((x) => (x == null ? "" : String(x))) : [];

      const normalizeFormulation = (item: SnapshotFormulation) => ({
        // Only the fields the Formulation model declares. Snapshot has
        // many extra parallel keys (plantId, plantName, clustersIds,
        // clustersNames, sourcePlantsIds/Names, packSizesIds/Names,
        // repackagingPackSizesIds/Names, repackagingPlantId/Name, etc.)
        // that mobx-keystone rejects — passing them makes every
        // `new Formulation(...)` throw and leaves the store empty.
        id: item.id,
        project: item.project,
        specs: item.specs ?? "",
        viscosity: item.viscosity ?? "",
        code: item.code ?? "",
        notes: item.notes ?? "",
        comments: item.comments ?? "",
        plantType:
          item.plantType === "master" || item.plantType === "plant"
            ? item.plantType
            : undefined,
        plantSplitCode: item.plantSplitCode ?? "",
        plantSplitPercent: asNumber(item.plantSplitPercent),
        isApproved: !!item.isApproved,
        isCltLocked: false,
        ordering: asNumber(item.ordering) ?? 0,
        density: asNumber(item.density) ?? 0,
        // Prefer the parallel `*Id` / `*Ids` fields (real DB IDs) over
        // the value-first name strings the snapshot puts in `plant`,
        // `clusters`, `countries`, `packSizes`, etc.
        plant: asId(item.plantId),
        repackagingPlant: asId(item.repackagingPlantId),
        sourcePlants: asIdArray(item.sourcePlantsIds),
        clusters: asIdArray(item.clustersIds),
        countries: asIdArray(item.countriesIds),
        packSizes: asIdArray(item.packSizesIds),
        repackagingPackSizes: asIdArray(item.repackagingPackSizesIds),
        // Preserve display names captured at lock time so rendering still
        // works when the referenced plant / cluster / country / packSize
        // asset has been deleted since the snapshot was taken.
        plantName: item.plantName ?? null,
        repackagingPlantName: item.repackagingPlantName ?? null,
        sourcePlantsNames: asStringArray(item.sourcePlantsNames),
        clustersNames: asStringArray(item.clustersNames),
        countriesNames: asStringArray(item.countriesNames),
        packSizesNames: asStringArray(item.packSizesNames),
        repackagingPackSizesNames: asStringArray(item.repackagingPackSizesNames),
        baseOilPercentages: (item.baseOilPercentages || []).map((p) =>
          normalizePercentage(p, "baseOil")
        ),
        additivePercentages: (item.additivePercentages || []).map((p) =>
          normalizePercentage(p, "additive")
        ),
        treatRatePercentages: (item.treatRatePercentages || []).map((p) =>
          normalizePercentage(p, "treatRate")
        ),
      });

      // Requirement model props use `undefined` for missing FKs, but the
      // snapshot type uses `null` — coerce so mobx-keystone strict prop
      // checks accept the payload.
      const normalizeRequirement = (item: SnapshotRequirement) => ({
        id: item.id,
        project: item.project,
        uniqueRef: item.uniqueRef ?? "",
        destinationCountry: item.destinationCountry ?? undefined,
        destinationCountryName: item.destinationCountryName ?? null,
        destinationClusterName: item.destinationClusterName ?? null,
        deliveryTerms: item.deliveryTerms ?? undefined,
        deliveryTermsName: item.deliveryTermsName ?? null,
        deliveryLocation: item.deliveryLocation ?? "",
        productSpecification: item.productSpecification ?? "",
        packSize: item.packSize ?? undefined,
        packSizeName: item.packSizeName ?? null,
        packageType: item.packageType ?? "",
        volumeYear1: item.volumeYear1 ?? null,
        volumeYear2: item.volumeYear2 ?? null,
        volumeYear3: item.volumeYear3 ?? null,
        volumeYear4: item.volumeYear4 ?? null,
        volumeYear5: item.volumeYear5 ?? null,
        packSizeRfq: item.packSizeRfq ?? "",
        altScenario: item.altScenario ?? "",
        isCltLocked: false,
      });

      // Track which snapshot rows we tried to hydrate and record the last
      // error per failing row for diagnostics. Whether to actually fire the
      // toast is decided by an OUTCOME check below: if every snapshot row
      // ended up as an entry in the store (regardless of whether some
      // internal side-effect threw), hydration succeeded from the user's
      // point of view and there is nothing to warn about.
      const formulationErrors = new Map<
        identifier,
        { message: string; stack?: string; itemDebug: unknown }
      >();
      snapshotFormulations.forEach((item) => {
        try {
          store.formulations.saveListItem(
            normalizeFormulation(item) as unknown as APIFormulation
          );
        } catch (err) {
          const e = err as Error;
          formulationErrors.set(item.id, {
            message: e?.message ?? String(err),
            stack: e?.stack,
            itemDebug: {
              id: item.id,
              code: item.code,
              specs: item.specs,
              plantId: item.plantId,
              plantType: item.plantType,
            },
          });
        }
      });
      const requirementErrors = new Map<
        identifier,
        { message: string; stack?: string; itemDebug: unknown }
      >();
      snapshotRequirements?.forEach((item) => {
        try {
          store.requirements.saveListItem(
            normalizeRequirement(item) as unknown as APIRequirement
          );
        } catch (err) {
          const e = err as Error;
          requirementErrors.set(item.id, {
            message: e?.message ?? String(err),
            stack: e?.stack,
            itemDebug: {
              id: item.id,
              uniqueRef: item.uniqueRef,
              destinationCountry: item.destinationCountry,
              packSize: item.packSize,
            },
          });
        }
      });
      store.formulations.commitCsv(project.id);

      // Outcome check: only warn if the store is genuinely missing rows the
      // snapshot claimed to have. A per-row `throw` that still populated the
      // store (e.g. from an inner side-effect on the second useAsync pass) is
      // harmless and must not scare the user with a red toast.
      const missingFormulations = snapshotFormulations.filter(
        (item) => !store.formulations.listItems.has(item.id)
      );
      const missingRequirements = (snapshotRequirements ?? []).filter(
        (item) => !store.requirements.listItems.has(item.id)
      );

      if (formulationErrors.size > 0 || requirementErrors.size > 0) {
        // Always log so diagnostics survive even when nothing user-visible
        // is broken.
        console.error(
          "[FormulationsTab] Snapshot hydration completed with errors:",
          {
            formulationErrors: Array.from(formulationErrors.entries()),
            requirementErrors: Array.from(requirementErrors.entries()),
            missingFormulationIds: missingFormulations.map((f) => f.id),
            missingRequirementIds: missingRequirements.map((r) => r.id),
          }
        );
      }

      if (missingFormulations.length > 0 || missingRequirements.length > 0) {
        Toast.danger(
          "Some formulation rows could not be restored from the project snapshot."
        );
      }
      return;
    }

    // Not locked, or locked without a snapshot (legacy projects locked
    // before the snapshot field existed) — fall back to the live API.
    store.formulations.commitCsv(project.id);
    await store.formulations.list(project.id);
    await store.requirements.list(project.id);
    store.formulations.commitCsv(project.id);
  }, [
    project?.id,
    project?.isLocked,
    // Re-run once the snapshot payload becomes available (it may arrive on a
    // later project refresh). Bug fix: `saveListItem` is now idempotent, so
    // re-hydrating an already-populated store no longer wipes percentages.
    !!project?.snapshot,
  ]);

  // Save formulations when save button is pressed.
  useSave(
    async () => {
      if (!project) {
        return;
      }

      if (!editable) {
        throw new Error("Formulations not editable");
      }

      try {
        await store.formulations.saveAll(project.id);
      } catch (e) {
        throw new Error(
          "Formulations not saved. Please ensure all input validations are resolved."
        );
      } finally {
        store.formulations.commitCsv(project.id);
      }
    },
    { saveOnUnmount: true }
  );

  const handleDownloadClick = useCallback(async () => {
    if (!project) {
      return;
    }

    project.setLoading("Preparing download...");

    const [mappedCsvHeadString, , colLength] = reparseMapHeadersFromCsvString(
      store.formulations.getHeadCsv(project.id, true) || ""
    );
    const mainHeader = [
      "",
      "",
      "",
      "",
      ...mappedCsvHeadString.split("\n")[0].split(",").slice(4),
    ].join(",");
    const oilsHeader = ["", "R Code", "RM Desc", "CRD Status"]
      .concat(new Array(colLength - 4))
      .join(",");
    const [mappedCsvBaseOilString] = reparseMapHeadersFromCsvString(
      store.formulations.getBaseOilsCsv(project.id, true) || ""
    );
    const [mappedCsvAdditiveString] = reparseMapHeadersFromCsvString(
      store.formulations.getAdditivesCsv(project.id, true) || ""
    );

    const mappedCsvString = addCsvSepHeader(
      `${mainHeader}\r\n${mappedCsvHeadString
        .split("\r\n")
        .slice(1)
        .join("\r\n")}\r\n${oilsHeader}\r\n${mappedCsvBaseOilString
        .split("\r\n")
        .slice(1)
        .join("\r\n")}\r\n${mappedCsvAdditiveString
        .split("\r\n")
        .slice(1)
        .join("\r\n")}`
    );

    const zip = new JSZip().file(`formulations.csv`, mappedCsvString);

    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `formulations-${project.id}.zip`);

    project.setNotLoading();
  }, [project, store.formulations]);

  const handleFileUpload = async (file: string) => {
    if (!project) {
      return;
    }

    project?.setLoading("Preparing upload...");

    try {
      await api.projects.uploadFormulations(project.id, {
        file,
      });
      project.setCostSheetDirty();
    } catch (e) {
      Toast.danger("Unable to process the CSV file.");
      return;
    } finally {
      project.setNotLoading();
    }

    Toast.success("CSV file successfully uploaded.");
    await store.projects.detail(project.id);
    await store.requirements.list(project.id);
    await store.formulations.list(project.id);
    store.formulations.commitCsv(project.id);
  };

  const ready = !state.loading;

  if (!ready) {
    return <Loading full />;
  }

  return (
    <CsvValidationErrorContext.Provider value={validationSummary}>
      <Toolbar>
        <FormulationFilter />
        <Button size="thin" onClick={handleDownloadClick}>
          <CloudDownloadOutlined /> Download CSV File
        </Button>
        {editable && <UploadButton onFileUpload={handleFileUpload} />}
        {editable && (
          <SearchFormulations onOpen={() => setShowApprovalPanel(false)} />
        )}
        {showSignOff && (
          <FormulationApprovalButton
            onOpen={() => {
              setShowApprovalPanel(true);
              setApprovalExpanded(true);
            }}
          />
        )}
        {editable && <GenerateCostSheetButton requireSignOff />}
        {project?.isLocked && <ProjectLockedLabel />}
        <ValidationDashboard validationChecks={[]} />
      </Toolbar>
      <ScrollSync vertical={false}>
        <SheetContainer>
          {(showApprovalPanel && showSignOff) ? (
            <FormulationApprovalPanel
              expanded={approvalExpanded}
              onToggle={() => setApprovalExpanded((value) => !value)}
              onClose={() => setShowApprovalPanel(false)}
            />
          ) : <>
          <FormulationsTabHead
            editable={editable}
            isSheetActive={activeSheet === "formulations"}
            onSheetActive={setActiveSheet}
          />
          <FormulationsTabBaseOils
            editable={editable}
            isSheetActive={activeSheet === "baseOils"}
            onSheetActive={setActiveSheet}
          />
          <FormulationsTabAdditives
            editable={editable}
            isSheetActive={activeSheet === "additives"}
            onSheetActive={setActiveSheet}
          />
          <FormulationsTabTreatRates
            editable={editable}
            isSheetActive={activeSheet === "treatRates"}
            onSheetActive={setActiveSheet}
          />
          <FormulationsTabTotals />
        </>}
        </SheetContainer>
      </ScrollSync>
    </CsvValidationErrorContext.Provider>
  );
};

export default observer(FormulationsTab);
